"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Trash2,
  Star,
  Building2,
  Phone,
  MapPin,
  PlayCircle,
  AlertCircle,
  Mail,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAppManager } from "@/context/AppManagerContext";
import PlansDialog from "@/components/PlansDialog";
import {
  type OrganizationsOutput,
  UserRoles,
  userRolesTranslations,
} from "@/interfaces";
import type { PlansOutput } from "@/interfaces/plan";
import { formatCep, formatDddPhone } from "@/util/formatters";

interface OrganizationCardProps {
  organization: OrganizationsOutput;
  onDeleteOrganization: (organization: string) => void;
  userRole: UserRoles;
  isCurrentOrg: boolean;
  startSession: (organizationId: string) => void;
  plans: PlansOutput[];
}

export function OrganizationCard({
  organization,
  onDeleteOrganization,
  userRole,
  isCurrentOrg,
  startSession,
  plans,
}: Readonly<OrganizationCardProps>) {
  const { setCurrentOrganization } = useAppManager();

  const hasPlan = !!organization.plan;
  const isOwner = userRole === UserRoles.OWNER;

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  return (
    <TooltipProvider>
      <Card
        className={`w-full relative group transition-all duration-300 ${
          isCurrentOrg
            ? `bg-gradient-to-r from-[#EE7B11]/5 to-transparent border-[#EE7B11] shadow-[0_0_0_1px] shadow-[#EE7B11]/20`
            : `hover:border-[#EE7B11]/30 hover:shadow-lg`
        }`}
      >
        {isCurrentOrg && (
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#EE7B11] rounded-full flex items-center justify-center shadow-lg">
            <Star className="h-4 w-4 text-white" />
          </div>
        )}
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex items-start gap-2 w-full sm:w-auto">
              <Building2 className="h-5 w-5 text-[#EE7B11] flex-shrink-0 mt-1" />
              <div className="flex-grow">
                <span className="text-lg leading-tight break-words">
                  {organization.name ?? "Organização sem nome"}
                </span>
                {isCurrentOrg && (
                  <Badge
                    variant="secondary"
                    className="bg-[#EE7B11]/10 text-[#EE7B11] border-[#EE7B11]/20 ml-2"
                  >
                    Atual
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteOrganization(organization.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Excluir organização</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2">
            {organization.phoneNumber && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate">
                  {formatDddPhone(
                    organization.ddd,
                    organization.phoneNumber,
                    organization.internationalCode
                  )}
                </span>
              </div>
            )}

            {organization.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate">{organization.email}</span>
              </div>
            )}

            {organization.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                <div className="truncate">
                  <p className="text-sm truncate">
                    {organization.address.line1}, {organization.address.number}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {organization.address.city}
                    {organization.address.zipCode
                      ? ` - ${formatCep(organization.address.zipCode)}`
                      : ""}
                  </p>
                </div>
              </div>
            )}
          </div>

          {(organization.phoneNumber ||
            organization.email ||
            organization.address) && (
            <div className="border-t border-[#EE7B11]/20" />
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Seu cargo:</span>
              <Badge
                variant="outline"
                className={`border-[#EE7B11]/30 ${
                  isCurrentOrg ? "bg-[#EE7B11]/10" : ""
                }`}
              >
                {userRolesTranslations[userRole]}
              </Badge>
            </div>
          </div>

          {hasPlan && <div className="border-t border-[#EE7B11]/20" />}

          {hasPlan && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Plano:</span>
                <Badge
                  variant="outline"
                  className={`border-[#EE7B11]/30 ${
                    organization.plan.activePlan
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {organization.plan.activePlan ? "Ativo" : "Inativo"}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="truncate">
                  <strong>Início:</strong>{" "}
                  {formatDate(organization.plan.startDate)}
                </p>
                <p className="truncate">
                  <strong>Término:</strong>{" "}
                  {formatDate(organization.plan.endDate)}
                </p>
              </div>
            </div>
          )}

          {(hasPlan || !hasPlan) && (
            <div className="border-t border-[#EE7B11]/20" />
          )}

          <div className="flex flex-col gap-4 items-center mt-4">
            {hasPlan ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => {
                        setCurrentOrganization(organization);
                        startSession(organization.id);
                      }}
                      className="bg-[#EE7B11] text-white hover:bg-[#D96E0F] w-full"
                    >
                      <PlayCircle className="h-5 w-5 mr-2" />
                      Iniciar sessão
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Iniciar sessão na organização</p>
                  </TooltipContent>
                </Tooltip>

                {isOwner && (
                  <PlansDialog organization={organization} plans={plans}>
                    <Button className="w-full bg-gradient-to-r from-[#EE7B11] to-[#F9A826] hover:from-[#D96E0F] hover:to-[#E89415] text-white font-medium text-lg py-6 animate-pulse">
                      <Sparkles className="h-5 w-5 mr-2" />
                      Fazer upgrade do plano
                    </Button>
                  </PlansDialog>
                )}
              </>
            ) : isOwner ? (
              <>
                <PlansDialog organization={organization} plans={plans}>
                  <Button className="w-full bg-gradient-to-r from-[#EE7B11] to-[#F9A826] hover:from-[#D96E0F] hover:to-[#E89415] text-white font-medium text-lg py-6 animate-pulse">
                    <Sparkles className="h-5 w-5 mr-2" />
                    Adquirir plano agora
                  </Button>
                </PlansDialog>
                <p className="text-sm text-center text-[#EE7B11]">
                  Desbloqueie todos os recursos com nosso plano premium!
                </p>
              </>
            ) : (
              <Alert className="mt-4 w-full">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Plano inativo</AlertTitle>
                <AlertDescription>
                  O plano desta organização está inativo no momento. Apenas o
                  proprietário pode adquirir um novo plano.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
